import { inject, Injectable, signal } from '@angular/core';
import { DeliveryTrackerService } from '@features/delivery-tracker/services/delivery-tracker.service';
import { ReminderTrackerPresenceService } from '@features/reminder-tracker/services/reminder-tracker-presence.service';
import type { ReminderTrackerPresenceEntry } from '@features/reminder-tracker/types/reminder-tracker-presence.types';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import type { ReminderTrackerPresenceItem } from '@kerberos-compliance/compliance-shared-ts/features/reminder-tracker/dtos/response.reminder-tracker-presence.dto';
import {
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorSeverityMap,
} from '@kerberos-compliance/compliance-shared-ui';
import dayjs from 'dayjs';
import { DeliveryTrackerPeriodsCacheService } from '../services/delivery-tracker-periods-cache.service';
import { DeliveryTrackerDetailStore } from '../stores/delivery-tracker-detail.store';
import { DeliveryTrackerDetailUiStore } from '../stores/delivery-tracker-detail-ui.store';

@Injectable()
export class DeliveryTrackerDetailFacade {
  private readonly deliveryTrackerService = inject(DeliveryTrackerService);

  private readonly reminderTrackerPresenceService = inject(ReminderTrackerPresenceService);

  private readonly deliveryTrackerDetailStore = inject(DeliveryTrackerDetailStore);

  private readonly deliveryTrackerDetailUiStore = inject(DeliveryTrackerDetailUiStore);

  private readonly periodsCacheService = inject(DeliveryTrackerPeriodsCacheService);

  private readonly asyncErrorHandler = createAsyncErrorHandler();

  private readonly loadingState = signal<boolean>(false);

  public readonly periods = this.deliveryTrackerDetailStore.items;

  public readonly selectedPeriodId = this.deliveryTrackerDetailStore.selectedPeriodId;

  public readonly selectedPeriod = this.deliveryTrackerDetailStore.selectedPeriod;

  public readonly loading = this.loadingState.asReadonly();

  public readonly reminderTrackerPresence = this.deliveryTrackerDetailStore.reminderTrackerPresence;

  public readonly reminderTrackerPresenceLoaded = this.deliveryTrackerDetailStore.reminderTrackerPresenceLoaded;

  public readonly hasActiveNotifiableUser = this.deliveryTrackerDetailStore.hasActiveNotifiableUser;

  public constructor() {
    const cachedPeriods = this.periodsCacheService.read();
    if (cachedPeriods?.length) {
      this.deliveryTrackerDetailStore.setPeriods([...cachedPeriods]);
    }
  }

  /**
   * Loads delivery tracker periods for an organization.
   * Auto-selects and expands the latest period.
   *
   * @param {UUID | undefined} organizationId - The organization ID to load periods for
   * @returns {Promise<void>} A promise that resolves once the periods have been loaded
   */
  public async loadDeliveryTrackerByOrganizationId(organizationId: UUID | undefined): Promise<void> {
    if (!organizationId) {
      return;
    }
    this.clearState();

    this.loadingState.set(true);
    try {
      await this.asyncErrorHandler(
        async () => {
          const [response] = await Promise.all([
            this.deliveryTrackerService.getDeliveryTrackersByOrganizationId(organizationId),
            this.loadReminderTrackerPresence(organizationId),
          ]);
          this.handlePeriodsResponse(response?.data);
        },
        {
          metadata: createErrorMetadata(
            'DeliveryTrackerDetailFacade',
            'Error loading delivery trackers for selected organization',
            ErrorSeverityMap.High,
          ),
          rethrow: true,
        },
      );
    } finally {
      this.loadingState.set(false);
    }
  }

  /**
   * Handles accordion expand/collapse state changes.
   * When a period is expanded, it is selected and its filter/UI state is synchronized.
   *
   * @param {UUID} trackerId - The tracker ID of the period being toggled
   * @param {boolean} isExpanded - Whether the period is being expanded
   * @returns {Promise<void>} A promise that resolves once the state change has been applied
   */
  public async onPeriodExpandChange(trackerId: UUID, isExpanded: boolean): Promise<void> {
    this.deliveryTrackerDetailUiStore.setTrackerIdExpanded(trackerId, isExpanded);

    if (isExpanded) {
      this.selectPeriod(trackerId);
    }
  }

  /**
   * Checks if a specific period is currently expanded.
   *
   * @param {UUID} trackerId - The tracker ID to check
   * @returns {boolean} True if the period is expanded
   */
  public isTrackerIdExpanded(trackerId: UUID): boolean {
    return this.deliveryTrackerDetailUiStore.isTrackerIdExpanded(trackerId);
  }

  /**
   * Clears all state across all stores. Called when the organization changes.
   *
   * @returns {void} This method does not return a value.
   */
  public clearState(): void {
    this.deliveryTrackerDetailStore.clearAll();
    this.deliveryTrackerDetailUiStore.clearAll();
    this.periodsCacheService.clear();
    this.loadingState.set(false);
  }

  /**
   * Selects a period and optionally expands it in the accordion.
   *
   * @param {UUID} trackerId - The tracker ID of the period to select
   * @param {{ expand?: boolean }} options - Optional config; set expand to true to auto-expand the accordion
   * @param {boolean} options.expand - Whether to expand the accordion for this tracker
   * @returns {void} This method does not return a value.
   */
  private selectPeriod(trackerId: UUID, options: { expand?: boolean } = {}): void {
    this.deliveryTrackerDetailStore.setSelectedPeriodId(trackerId);

    if (options.expand) {
      this.deliveryTrackerDetailUiStore.setTrackerIdExpanded(trackerId, true);
    }
  }

  /**
   * Handles the response from loading delivery tracker periods. Sorts periods by year and auto-selects the latest period.
   *
   * @param {ResponseDeliveryTrackerCacheDto[] | undefined} data - The array of delivery tracker periods returned from the API, or undefined if the request failed or returned no data.
   * @returns {void} This method does not return a value, but it updates the state of the delivery tracker detail store with the loaded periods and selected period ID.
   */
  private handlePeriodsResponse(data: ResponseDeliveryTrackerCacheDto[] | undefined): void {
    if (!data?.length) {
      this.deliveryTrackerDetailStore.setPeriods([]);
      this.periodsCacheService.write([]);
      this.deliveryTrackerDetailStore.setSelectedPeriodId(undefined);
      return;
    }

    const sortedPeriods = this.sortPeriodsByYear(data);
    this.deliveryTrackerDetailStore.setPeriods(sortedPeriods);
    this.periodsCacheService.write(sortedPeriods);

    if (sortedPeriods.length > 0) {
      this.selectPeriod(sortedPeriods[0].id, { expand: true });
    }
  }

  /**
   * Sorts periods by year descending (latest first).
   * Falls back to current year if periodStart is not available.
   *
   * @param {ResponseDeliveryTrackerCacheDto[]} periods - The periods to sort
   * @returns {ResponseDeliveryTrackerCacheDto[]} Periods sorted by year descending
   */
  private sortPeriodsByYear(periods: ResponseDeliveryTrackerCacheDto[]): ResponseDeliveryTrackerCacheDto[] {
    const currentYear = dayjs().year();
    return periods.toSorted((a, b) => {
      const yearA = a.periodStart ? dayjs(a.periodStart).year() : currentYear;
      const yearB = b.periodStart ? dayjs(b.periodStart).year() : currentYear;
      return yearB - yearA;
    });
  }

  /**
   * Loads the Risk Analysis reminder tracker presence for the organization and stores it as a map keyed by delivery tracker ID.
   * Routed through the shared async error handler so failures are reported via the project pipeline without
   * interrupting the page (the presence badge is supplementary).
   *
   * @param {UUID} organizationId - The organization to scope the lookup to.
   * @returns {Promise<void>} A promise that resolves once the presence map has been stored.
   */
  private async loadReminderTrackerPresence(organizationId: UUID): Promise<void> {
    const response = await this.asyncErrorHandler(
      async () => this.reminderTrackerPresenceService.getRiskAnalysisPresenceByOrganization(organizationId),
      {
        metadata: createErrorMetadata(
          'DeliveryTrackerDetailFacade',
          'loadReminderTrackerPresence',
          ErrorSeverityMap.Low,
        ),
        // The presence badge is supplementary, so a failure is reported without a toast
        silent: true,
      },
    );
    if (!response) {
      return;
    }
    this.deliveryTrackerDetailStore.setReminderTrackerPresence(
      this.buildReminderTrackerPresenceMap(response.items),
      response.hasActiveNotifiableUser,
    );
  }

  /**
   * Converts the presence response into a map keyed by delivery tracker ID.
   *
   * A delivery tracker legitimately carries several reminder trackers — a closed-out Done row plus the
   * Pending row created on re-onboarding — so the count is not an anomaly and is not checked here. The
   * backend owns the "at most one Pending row per delivery tracker" invariant and reports violations
   * itself, with the offending tracker ids.
   *
   * @param {ReminderTrackerPresenceItem[]} items - The raw presence items returned by the backend.
   * @returns {Map<UUID, ReminderTrackerPresenceEntry>} The presence map.
   */
  private buildReminderTrackerPresenceMap(
    items: ReminderTrackerPresenceItem[],
  ): Map<UUID, ReminderTrackerPresenceEntry> {
    const map = new Map<UUID, ReminderTrackerPresenceEntry>();
    for (const item of items) {
      map.set(item.deliveryTrackerId, {
        reminderTrackerStatus: item.reminderTrackerStatus,
        reminderTrackerCreatedAt: item.reminderTrackerCreatedAt,
        reminderTrackerUpdatedAt: item.reminderTrackerUpdatedAt,
        reminderTrackerNextReminderAt: item.reminderTrackerNextReminderAt,
        reminderTrackerStatusChangedAt: item.reminderTrackerStatusChangedAt,
      });
    }
    return map;
  }
}
